/**
 * Phase 2 - Discovery as a federated remote.
 *
 * This module is exposed via `@module-federation/vite` as
 * `discovery_remote/routes`. A host calls
 * `loadRemote('discovery_remote/routes')` and renders the returned
 * `DiscoveryRemoteApp` component to mount Discovery's full route tree
 * inside the host's chrome.
 *
 * Two hosts exist:
 *   - `apps/shell-app` (demo-only since the 2026-07-12 Core-as-host
 *     decision): mounts the remote at the domain ROOT with no props -
 *     behaviour identical to the original Day-6-to-10 seam.
 *   - Core's app (Workstream C, the customer-facing inline mount at
 *     `company.foctta.com/discovery/*`): passes `basepath` +
 *     `getHandoffToken` per the contract in
 *     docs/handoff/C-SPIKE-DISCOVERY-REMOTE-CONTRACT-2026-07-12.md.
 *
 * Auth (exchange-once-at-mount, Core's Q2 answer 2026-07-12):
 *   Core's session JWT is HS256 and never crosses the product boundary.
 *   Instead the HOST fetches a 5-min RS256 handoff token from Core
 *   (`POST /v1/discovery/handoff`) and passes a GETTER for it via
 *   `getHandoffToken`. At mount, if the remote has no live Discovery
 *   session of its own, it calls the getter and exchanges the token at
 *   Discovery's `/v1/auth/exchange` (same endpoint the redirect bridge
 *   uses, proven in prod), then persists the Discovery session under
 *   Discovery's OWN localStorage keys (`foctta.auth.*` - verified
 *   distinct from Core's `auth-storage` Zustand blob, so the two
 *   sessions coexist on the shared origin). Handoff tokens are
 *   single-use: the getter must mint a FRESH token per call.
 *
 * Singleton contract (aligned with Core's pins, verified 2026-07-12):
 *   - react ^18.3, react-dom ^18.3 - shared (host's instance wins)
 *   - @tanstack/react-router ^1.90 - library shared; router INSTANCES
 *     stay per-remote (two routers on one window.location; the remote's
 *     is namespaced under `basepath` so they don't fight)
 *   - @tanstack/react-query ^5.62 - library shared; host MAY provide its
 *     QueryClient via `hostQueryClient`, else the remote uses its own
 *   - @foctta/ui-shell + @foctta/design-tokens - Core does NOT adopt
 *     these; declared shared for the shell-app host, but when the host
 *     doesn't provide them the MF runtime falls back to the remote's own
 *     bundled copy (the documented divergence).
 */
import { StrictMode, useEffect, useMemo, useState, type ReactNode } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { createDiscoveryRouter, router as standaloneRouter } from './router';
import { exchangeBridgeToken, getAuthToken, setAuth } from './lib/api';
import { installCrossTabSync } from './stores/ui';
import './styles/globals.css';

// One-shot side-effect - runs the first time the remote module is
// imported, regardless of which mode (standalone vs federated). Safe to
// call multiple times; the installer is idempotent.
installCrossTabSync();

/**
 * Discovery's local QueryClient. Used when running standalone OR when
 * mounted as a remote without a host-provided QueryClientProvider.
 */
const localQueryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, staleTime: 30_000, retry: 1 },
  },
});

/**
 * Router-per-basepath cache. Module-level so React 18 StrictMode's
 * double-mount (and any host remount) reuses the SAME router instance
 * instead of constructing a second one against the same history - the
 * undefined-basepath entry is the standalone singleton itself.
 */
const routerCache = new Map<string, ReturnType<typeof createDiscoveryRouter>>();
function routerFor(basepath?: string) {
  if (!basepath) return standaloneRouter;
  let r = routerCache.get(basepath);
  if (!r) {
    r = createDiscoveryRouter(basepath);
    routerCache.set(basepath, r);
  }
  return r;
}

export interface DiscoveryRemoteAppProps {
  /**
   * Optional host-provided QueryClient. If supplied, the remote uses it
   * (enabling cross-product cache reuse). If omitted, the remote uses
   * its own client.
   */
  hostQueryClient?: QueryClient;
  /**
   * Path prefix the host mounts the remote under (e.g. '/discovery' for
   * the Core-as-host inline mount). Omit for root-mounted hosts
   * (shell-app demo) and standalone. TanStack Router prefixes every
   * resolved href/match with it, so the remote's internal '/activity'
   * renders at '/discovery/activity' on the host origin.
   */
  basepath?: string;
  /**
   * Exchange-once-at-mount auth (Core-as-host). Must return a FRESH
   * Core-minted RS256 handoff token (they are single-use, 5-min TTL).
   * Called only when the remote has no live Discovery session; the
   * remote exchanges it at /v1/auth/exchange and persists its own
   * session. Omit for standalone / shell-app (localStorage session or
   * the /auth/bridge redirect flow handles auth there).
   */
  getHandoffToken?: () => Promise<string>;
}

type MountAuthState =
  | { phase: 'ready' }
  | { phase: 'exchanging' }
  | { phase: 'error'; detail: string };

export function DiscoveryRemoteApp({
  hostQueryClient,
  basepath,
  getHandoffToken,
}: DiscoveryRemoteAppProps = {}) {
  const client = hostQueryClient ?? localQueryClient;
  const remoteRouter = useMemo(() => routerFor(basepath), [basepath]);

  // Exchange-once-at-mount: skip entirely when a Discovery session
  // already exists (getAuthToken clears + returns null when expired) or
  // when no getter was supplied (standalone/demo hosts).
  const [auth, setAuthState] = useState<MountAuthState>(() =>
    !getHandoffToken || getAuthToken() ? { phase: 'ready' } : { phase: 'exchanging' },
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (auth.phase !== 'exchanging' || !getHandoffToken) return;
    let cancelled = false;
    (async () => {
      try {
        const handoffToken = await getHandoffToken();
        const res = await exchangeBridgeToken(handoffToken);
        if (cancelled) return;
        setAuth(res.token, res.user, res.expiresAt);
        setAuthState({ phase: 'ready' });
      } catch (err) {
        if (cancelled) return;
        setAuthState({
          phase: 'error',
          detail: err instanceof Error ? err.message : String(err),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // `attempt` re-runs the exchange when the user clicks retry.
  }, [auth.phase, getHandoffToken, attempt]);

  let content: ReactNode;
  if (auth.phase === 'exchanging') {
    content = (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
        Signing you in to FOCTTA Discovery…
      </div>
    );
  } else if (auth.phase === 'error') {
    content = (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 p-6 text-center">
        <div className="text-base font-semibold">Could not sign you in to Discovery</div>
        <div className="max-w-md text-sm text-muted-foreground">{auth.detail}</div>
        <button
          type="button"
          className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-accent"
          onClick={() => {
            setAttempt((n) => n + 1);
            setAuthState({ phase: 'exchanging' });
          }}
        >
          Try again
        </button>
      </div>
    );
  } else {
    content = (
      <QueryClientProvider client={client}>
        <RouterProvider router={remoteRouter} />
        <Toaster position="top-right" theme="system" richColors closeButton />
      </QueryClientProvider>
    );
  }

  // ALL of the remote's DOM lives under `#discovery-remote-root` so the remote
  // build's container-scoped Tailwind utilities (tailwind.config `important:
  // '#discovery-remote-root'`, Blocker B) actually apply - and, being
  // ID-scoped, beat the HOST's preflight on our own elements (fixes the 16px
  // headings the earlier cascade-layer approach caused). Standalone uses
  // main.tsx, not this entry, so it is unaffected.
  return (
    <StrictMode>
      <div id="discovery-remote-root">{content}</div>
    </StrictMode>
  );
}

export default DiscoveryRemoteApp;
